import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Wheelchair, Truck, Path, CalendarBlank, Airplane } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format, parse, isValid } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SMSComplianceNotice from "../components/SMSComplianceNotice";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { autoPrefixUS } from "../components/PhoneInput";

const defaultForm = {
  facility_name: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  patient_first_name: "",
  patient_middle_initial: "",
  patient_last_name: "",
  patient_email: "",
  patient_phone: "",
  mobility_type: "ambulatory",
  trip_type: "appointment",
  pickup_address: "",
  dropoff_address: "",
  pickup_date: "",
  pickup_time: "",
  return_trip: false,
  notes: "",
  flight_number: "",
  flight_scheduled_arrival: "",
  airport_role: "",
  terminal: "",
  sms_opt_in: false,
};

const input =
  "w-full rounded-md border border-[#E2E0D8] bg-white px-4 py-3 text-sm text-slate_navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest transition";
const labelCls = "block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy mb-2";

export default function BookTrip() {
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState(null);
  const [flightInfo, setFlightInfo] = useState(null);
  const [flightLoading, setFlightLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live fare estimator — pings /api/bookings/quote when key fields are populated
  useEffect(() => {
    const ready =
      form.pickup_address && form.dropoff_address && form.mobility_type && form.trip_type;
    if (!ready) { setQuote(null); return; }
    let canceled = false;
    const t = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const { data } = await apiClient.post("/bookings/quote", {
          facility_name: form.facility_name || undefined,
          mobility_type: form.mobility_type,
          pickup_address: form.pickup_address,
          dropoff_address: form.dropoff_address,
          return_trip: !!form.return_trip,
        });
        if (!canceled) setQuote(data);
      } catch {
        if (!canceled) setQuote(null);
      } finally {
        if (!canceled) setQuoteLoading(false);
      }
    }, 650);
    return () => { canceled = true; clearTimeout(t); };
  }, [
    form.facility_name,
    form.mobility_type,
    form.pickup_address,
    form.dropoff_address,
    form.return_trip,
    form.trip_type,
  ]);

  // Auto-lookup flight info when flight number + date are set
  useEffect(() => {
    const fn = (form.flight_number || "").trim();
    if (form.trip_type !== "airport" || !fn || !form.pickup_date) return;
    let canceled = false;
    setFlightLoading(true);
    apiClient.get(`/flights/${encodeURIComponent(fn)}`, { params: { flight_date: form.pickup_date } })
      .then(({ data }) => { if (!canceled) setFlightInfo(data); })
      .catch(() => { if (!canceled) setFlightInfo({ source: "error" }); })
      .finally(() => { if (!canceled) setFlightLoading(false); });
    return () => { canceled = true; };
  }, [form.flight_number, form.pickup_date, form.trip_type]);

  // When a scheduled arrival is picked and user hasn't set pickup_time yet, suggest +30 min
  const applyFlightBuffer = () => {
    const arr = form.flight_scheduled_arrival;
    if (!arr) return;
    const [h, m] = arr.split(":").map((x) => parseInt(x, 10));
    if (!isFinite(h) || !isFinite(m)) return;
    const total = h * 60 + m + 30;
    const nh = String(Math.floor(total / 60) % 24).padStart(2, "0");
    const nm = String(total % 60).padStart(2, "0");
    update("pickup_time", `${nh}:${nm}`);
    toast.success(`Pickup time set to ${nh}:${nm} (landing + 30 min)`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pickup_verified || !form.dropoff_verified) {
      toast.error("Please pick both pickup and drop-off addresses from the suggestions so we can validate them.");
      return;
    }
    setSubmitting(true);
    try {
      // Strip empty strings — backend Pydantic Literal fields reject "" but accept null/undefined.
      // Also drop airport-only fields entirely when the trip type isn't "airport".
      const cleaned = Object.entries(form).reduce((acc, [k, v]) => {
        if (v === "" || v === null || v === undefined) return acc;
        if (["pickup_verified", "dropoff_verified", "pickup_lat", "pickup_lng", "dropoff_lat", "dropoff_lng"].includes(k)) return acc;
        if (form.trip_type !== "airport" && ["flight_number", "flight_scheduled_arrival", "airport_role", "terminal"].includes(k)) return acc;
        acc[k] = v;
        return acc;
      }, {});
      const { data } = await apiClient.post("/bookings", cleaned);
      setSuccessId(data.id);
      toast.success("Trip request submitted.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit trip.");
    } finally {
      setSubmitting(false);
    }
  };

  if (successId) {
    return (
      <main className="min-h-[80vh] bg-bone">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-24 text-center" data-testid="booking-success">
          <div className="mx-auto h-14 w-14 rounded-md bg-forest text-white inline-flex items-center justify-center">
            <CheckCircle weight="fill" size={28} />
          </div>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-bold text-slate_navy leading-tight">
            Trip request received.
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Thank you. Our dispatch team will review the request and follow up with your facility
            contact shortly.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#E2E0D8] bg-white px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold text-brandblue-deep">
            Reference · {successId.slice(0, 8)}
          </div>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Link to="/" className="text-sm font-semibold text-forest hover:text-forest-hover">
              Back to home
            </Link>
            <button
              onClick={() => {
                setForm(defaultForm);
                setSuccessId(null);
              }}
              className="inline-flex items-center gap-2 rounded-md bg-forest hover:bg-forest-hover text-white px-5 py-2.5 text-sm font-semibold"
              data-testid="book-another-btn"
            >
              Book another trip <ArrowRight weight="bold" size={14} />
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-bone">
      <section className="bg-slate_navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm">
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="mt-6 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brandblue">
                Booking portal
              </div>
              <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">
                Request a non-emergency medical trip.
              </h1>
              <p className="mt-6 text-base text-white/70 max-w-xl leading-relaxed">
                Provide patient, facility, and trip details below. Our dispatch team will confirm
                pricing, classification, and pickup details with the facility contact.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-px border border-white/10 rounded-md overflow-hidden bg-white/5">
              {[
                { i: Wheelchair, l: "Mobility assist" },
                { i: Truck, l: "Vetted fleet" },
                { i: Path, l: "Full tracking" },
              ].map(({ i: Ic, l }) => (
                <div key={l} className="bg-slate_navy p-5">
                  <Ic weight="duotone" size={22} className="text-brandblue" />
                  <div className="mt-3 text-sm font-medium">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <form onSubmit={submit} className="space-y-12" data-testid="booking-form">
            {/* Facility */}
            <div>
              <FormSectionTitle num="01" title="Facility & requester" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Facility name</label>
                  <input required value={form.facility_name} onChange={(e) => update("facility_name", e.target.value)} className={input} data-testid="facility-name-input" placeholder="Example Regional Hospital" />
                </div>
                <div>
                  <label className={labelCls}>Contact name</label>
                  <input required value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className={input} data-testid="contact-name-input" placeholder="Jane Smith" />
                </div>
                <div>
                  <label className={labelCls}>Contact phone</label>
                  <input required value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)}
                         onBlur={() => update("contact_phone", autoPrefixUS(form.contact_phone))}
                         className={input} data-testid="contact-phone-input" placeholder="(940) 000-0000" />
                </div>
                <div>
                  <label className={labelCls}>Contact email</label>
                  <input required type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} className={input} data-testid="contact-email-input" placeholder="coordinator@facility.com" />
                </div>
              </div>
            </div>

            {/* Patient */}
            <div>
              <FormSectionTitle num="02" title="Patient" />
              <div className="grid sm:grid-cols-12 gap-4">
                <div className="sm:col-span-5">
                  <label className={labelCls}>First name</label>
                  <input
                    required
                    value={form.patient_first_name}
                    onChange={(e) => update("patient_first_name", e.target.value)}
                    className={input}
                    data-testid="patient-first-name-input"
                    placeholder="First name"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>M.I.</label>
                  <input
                    value={form.patient_middle_initial}
                    onChange={(e) => update("patient_middle_initial", e.target.value.replace(/[^A-Za-z]/g, "").slice(0, 1).toUpperCase())}
                    className={input}
                    data-testid="patient-middle-initial-input"
                    placeholder="M"
                    maxLength={1}
                  />
                </div>
                <div className="sm:col-span-5">
                  <label className={labelCls}>Last name</label>
                  <input
                    required
                    value={form.patient_last_name}
                    onChange={(e) => update("patient_last_name", e.target.value)}
                    className={input}
                    data-testid="patient-last-name-input"
                    placeholder="Last name"
                  />
                </div>
                <div className="sm:col-span-6">
                  <label className={labelCls}>Patient email</label>
                  <input
                    type="email"
                    value={form.patient_email}
                    onChange={(e) => update("patient_email", e.target.value)}
                    className={input}
                    data-testid="patient-email-input"
                    placeholder="patient@example.com"
                  />
                </div>
                <div className="sm:col-span-6">
                  <label className={labelCls}>Patient phone</label>
                  <input
                    type="tel"
                    value={form.patient_phone}
                    onChange={(e) => update("patient_phone", e.target.value)}
                    onBlur={() => update("patient_phone", autoPrefixUS(form.patient_phone))}
                    className={input}
                    data-testid="patient-phone-input"
                    placeholder="(940) 000-0000"
                  />
                </div>
                <div className="sm:col-span-12">
                  <label className={labelCls}>Mobility type</label>
                  <select required value={form.mobility_type} onChange={(e) => update("mobility_type", e.target.value)} className={input} data-testid="mobility-type-select">
                    <option value="ambulatory">Ambulatory</option>
                    <option value="wheelchair">Wheelchair</option>
                    <option value="stretcher">Stretcher</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Trip */}
            <div>
              <FormSectionTitle num="03" title="Trip details" />
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Trip type</label>
                  <select required value={form.trip_type} onChange={(e) => update("trip_type", e.target.value)} className={input} data-testid="trip-type-select">
                    <option value="appointment">Appointment</option>
                    <option value="dialysis">Dialysis</option>
                    <option value="discharge">Hospital discharge</option>
                    <option value="facility_to_facility">Facility to facility</option>
                    <option value="airport">Airport (flight-aware)</option>
                    <option value="school">School / IDD route</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-3 text-sm text-slate_navy" data-testid="return-trip-label">
                    <input type="checkbox" checked={form.return_trip} onChange={(e) => update("return_trip", e.target.checked)} className="h-4 w-4 rounded border-[#E2E0D8] text-forest focus:ring-forest" data-testid="return-trip-checkbox" />
                    Return trip required
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Pickup address</label>
                  <AddressAutocomplete
                    value={form.pickup_address}
                    onChange={(v) => { update("pickup_address", v); update("pickup_verified", false); }}
                    onPlace={(p) => { update("pickup_address", p.formatted_address); update("pickup_lat", p.lat); update("pickup_lng", p.lng); update("pickup_verified", true); }}
                    placeholder="Start typing pickup street, city…"
                    testid="pickup-address-input"
                    className={input}
                  />
                  {!form.pickup_verified && form.pickup_address && (
                    <div className="text-[11px] text-red-600 mt-1">Pick a suggestion from the list to verify this address.</div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Drop-off address</label>
                  <AddressAutocomplete
                    value={form.dropoff_address}
                    onChange={(v) => { update("dropoff_address", v); update("dropoff_verified", false); }}
                    onPlace={(p) => { update("dropoff_address", p.formatted_address); update("dropoff_lat", p.lat); update("dropoff_lng", p.lng); update("dropoff_verified", true); }}
                    placeholder="Start typing drop-off street, city…"
                    testid="dropoff-address-input"
                    className={input}
                  />
                  {!form.dropoff_verified && form.dropoff_address && (
                    <div className="text-[11px] text-red-600 mt-1">Pick a suggestion from the list to verify this address.</div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Pickup date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="pickup-date-trigger"
                        className={`${input} flex items-center justify-between text-left ${form.pickup_date ? "text-slate_navy" : "text-slate-400"}`}
                      >
                        {form.pickup_date
                          ? (() => {
                              const d = parse(form.pickup_date, "yyyy-MM-dd", new Date());
                              return isValid(d) ? format(d, "EEE, MMM d, yyyy") : form.pickup_date;
                            })()
                          : "Select pickup date"}
                        <CalendarBlank size={16} className="text-slate-500 shrink-0 ml-2" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start" data-testid="pickup-date-popover">
                      <Calendar
                        mode="single"
                        selected={
                          form.pickup_date
                            ? (() => {
                                const d = parse(form.pickup_date, "yyyy-MM-dd", new Date());
                                return isValid(d) ? d : undefined;
                              })()
                            : undefined
                        }
                        onSelect={(d) => d && update("pickup_date", format(d, "yyyy-MM-dd"))}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return d < today;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {/* Keep a hidden input so HTML validation still fires */}
                  <input type="hidden" required value={form.pickup_date} readOnly data-testid="pickup-date-input" />
                </div>
                <div>
                  <label className={labelCls}>Pickup time</label>
                  <input required type="time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} className={input} data-testid="pickup-time-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Notes</label>
                  <textarea rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} className={input} data-testid="notes-input" placeholder="Special needs, oxygen, escort, etc." />
                </div>
              </div>
            </div>

            {/* Airport panel — shown when trip_type is airport */}
            {form.trip_type === "airport" && (
              <div data-testid="airport-panel" className="rounded-xl border border-brandblue/30 bg-brandblue/5 p-5">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-brandblue text-white grid place-items-center">
                    <Airplane size={18} weight="fill" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">Flight-aware pickup</div>
                    <div className="font-heading text-base font-bold text-slate_navy">Add a flight # and we'll auto-calc the pickup time</div>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Airport role</label>
                    <select value={form.airport_role} onChange={(e) => update("airport_role", e.target.value)} className={input} data-testid="airport-role">
                      <option value="">Select…</option>
                      <option value="pickup_at_airport">Pickup FROM airport</option>
                      <option value="dropoff_at_airport">Drop-off TO airport</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Flight number</label>
                    <input value={form.flight_number} onChange={(e) => update("flight_number", e.target.value.toUpperCase())} className={input} placeholder="e.g. AA1234" data-testid="flight-number-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Scheduled landing / departure time</label>
                    <input type="time" value={form.flight_scheduled_arrival} onChange={(e) => update("flight_scheduled_arrival", e.target.value)} className={input} data-testid="flight-arrival-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Terminal (optional)</label>
                    <input value={form.terminal} onChange={(e) => update("terminal", e.target.value)} className={input} placeholder="e.g. D" data-testid="terminal-input" />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={applyFlightBuffer}
                    disabled={!form.flight_scheduled_arrival}
                    data-testid="apply-flight-buffer"
                    className="inline-flex items-center gap-2 bg-brandblue hover:bg-brandblue-hover text-white text-xs font-semibold px-3 py-2 rounded-md disabled:opacity-50"
                  >
                    Use landing + 30 min as pickup
                  </button>
                  {flightLoading && <span className="text-xs text-slate-500">Checking flight…</span>}
                  {flightInfo && flightInfo.source === "manual" && (
                    <span className="text-xs text-slate-500">{flightInfo.note}</span>
                  )}
                  {flightInfo && flightInfo.source === "aviationstack" && (
                    <span className="text-xs text-brandblue font-semibold">
                      {flightInfo.airport ? `${flightInfo.airport} · ` : ""}
                      {flightInfo.scheduled_arrival ? `sched ${flightInfo.scheduled_arrival}` : ""}
                      {flightInfo.delay_minutes ? ` (+${flightInfo.delay_minutes}m delay)` : ""}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Live fare estimator */}
            {(quote || quoteLoading) && (
              <div
                className="rounded-xl border border-forest/30 bg-forest/5 p-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center"
                data-testid="fare-quote-card"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-forest">
                    {quote?.facility_contract ? "Facility-contract fare" : "Estimated fare"}
                  </div>
                  {quoteLoading && !quote && (
                    <div className="mt-2 text-sm text-slate_navy">Calculating fare…</div>
                  )}
                  {quote && (
                    <>
                      <div className="font-heading text-4xl font-black text-forest leading-none mt-2" data-testid="fare-price">
                        ${Number(quote.price_usd).toFixed(2)}
                      </div>
                      {quote.breakdown && (
                        <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                          {quote.breakdown.miles ? (
                            <div>{quote.breakdown.miles} mi × ${quote.breakdown.per_mile_usd}/mi = ${quote.breakdown.mileage_cost_usd}</div>
                          ) : null}
                          <div>Base ${quote.breakdown.base_fee_usd}</div>
                          {quote.breakdown.mobility_surcharge_usd > 0 && (
                            <div>{form.mobility_type === "wheelchair" ? "Wheelchair" : "Stretcher"} surcharge ${quote.breakdown.mobility_surcharge_usd}</div>
                          )}
                          {quote.breakdown.return_trip && (
                            <div className="font-semibold">× 2 (round trip)</div>
                          )}
                        </div>
                      )}
                      <p className="mt-2 text-[11px] text-slate-500 italic">
                        Estimate based on point-to-point mileage{quote.facility_contract ? " using your contracted rate card." : "."} Final fare may vary with actual route, waits, and additional accessory needs.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* SMS opt-in — CTIA / toll-free carrier compliance */}
            <div className="rounded-xl border-2 border-brandblue/40 bg-brandblue/5 p-5" data-testid="sms-opt-in-section">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue mb-2">Sign up for SMS notifications</div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sms_opt_in}
                  onChange={(e) => update("sms_opt_in", e.target.checked)}
                  data-testid="sms-opt-in-checkbox"
                  className="mt-1 h-5 w-5 rounded border-2 border-brandblue/50 text-brandblue focus:ring-brandblue cursor-pointer"
                />
                <span className="text-sm text-slate_navy leading-relaxed">
                  <b>I agree to receive automated transactional SMS messages from MedexOVA Medical Transportation</b> regarding my trip request, driver assignment, ETA, and trip status updates at the phone number provided above.
                  {" "}Message frequency may vary. Standard message and data rates may apply. Reply <b>STOP</b> to opt out at any time. Reply <b>HELP</b> for assistance. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes. View our
                  {" "}<a href="/sms-terms" target="_blank" rel="noreferrer" className="text-brandblue underline font-semibold">SMS Terms</a>
                  {" "}and
                  {" "}<a href="/privacy" target="_blank" rel="noreferrer" className="text-brandblue underline font-semibold">Privacy Policy</a>.
                </span>
              </label>
              {!form.sms_opt_in && (
                <p className="mt-3 text-[11px] text-terracotta font-semibold">
                  SMS consent is required to submit a trip request — we use SMS to confirm your driver and notify you of arrival.
                </p>
              )}
            </div>

            <SMSComplianceNotice />
            <div className="flex items-center justify-between pt-4 border-t border-[#E2E0D8]">
              <p className="text-xs text-slate-500 max-w-md">
                Submissions are received by our dispatch team. For urgent matters, email info@medexova.com.
              </p>
              <button
                type="submit"
                disabled={submitting || !form.sms_opt_in}
                data-testid="booking-submit-btn"
                className="inline-flex items-center gap-2 rounded-md bg-forest hover:bg-forest-hover disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-colors"
              >
                {submitting ? "Submitting…" : "Submit Trip Request"} <ArrowRight weight="bold" size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

function FormSectionTitle({ num, title }) {
  return (
    <div className="mb-6 flex items-end gap-4 border-b border-[#E2E0D8] pb-4">
      <div className="font-heading text-4xl font-black text-forest">{num}</div>
      <h2 className="font-heading text-xl sm:text-2xl font-semibold text-slate_navy">{title}</h2>
    </div>
  );
}
